import React from 'react';
import clsx from 'clsx';
import { Upload } from '@mui/icons-material';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDoNotShowAgain: (checked: boolean) => void;
}

const HowToRecordModal: React.FC<ModalProps> = ({ isOpen, onClose, onDoNotShowAgain }) => {
  const [doNotShowAgain, setDoNotShowAgain] = React.useState(false);

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onDoNotShowAgain(doNotShowAgain);
      onClose();
    }
  };

  const handleGetStarted = () => {
    onDoNotShowAgain(doNotShowAgain);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={handleOverlayClick}
    >
      <div className="bg-white rounded-lg p-8 max-w-2xl w-full mx-4 relative">
        {/* Close button */}
        <button
          onClick={() => {
            onDoNotShowAgain(doNotShowAgain);
            onClose();
          }}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>

        {/* Header */}
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">How to Record Your Meeting</h2>
          <p className="text-gray-600">Follow these simple steps to transcribe your meetings</p>
        </div>

        {/* Steps */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* Step 1: Record */}
          <div className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="8" fill="#EF4444"/>
                <circle cx="12" cy="12" r="4" fill="#ffffff"/>
              </svg>
            </div>
            <h3 className="font-semibold text-gray-800 mb-2">100% Easy to Use</h3>
            <p className="text-sm text-gray-600">Simply hit record button and proceed with your meetings while we take notes. Keep the browser on!</p>
          </div>

           {/* Step 2: Download */}
          <div className="text-center">
            <div className="w-16 h-16 bg-green-100 text-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <Upload />
            </div>
            <h3 className="font-semibold text-gray-800 mb-2">Upload</h3>
            <p className="text-sm text-gray-600">Done with your appointment? Hit Upload and our transcript model handles upload and transcribing of your meeting in a jiffy</p>
          </div>

          {/* Step 3: Transcribe */}
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 11H15M9 15H15M12 3L16 7H8L12 3Z" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M3 7H21V19C21 20.1046 20.1046 21 19 21H5C3.89543 21 3 20.1046 3 19V7Z" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h3 className="font-semibold text-gray-800 mb-2">Transcription!</h3>
            <p className="text-sm text-gray-600">Our AI-powered transcription converts both participant's speech to medical-templated documentations for you to edit or download</p>
          </div>

         
        </div>

        {/* Checkbox and button */}
        <div className="flex items-center justify-between">
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={doNotShowAgain}
              onChange={(e) => setDoNotShowAgain(e.target.checked)}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="text-sm text-gray-600">Don't show this again</span>
          </label>

          <button
            onClick={handleGetStarted}
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
          >
            Get Started
          </button>
        </div>
      </div>
    </div>
  );
};

export default HowToRecordModal;
